import { Button, CircularProgress, Drawer, Typography } from "@mui/material";
import { useUnit } from "effector-react";
import { Suspense, lazy, useState } from "react";

import { STEP_TUTORIAL, type StepTutorialTypes } from "../constants/tutorial";
import {
  $gameConfig,
  $playerData,
  buildValleyBlock,
  getAmountOfBuildings,
} from "../store";
import type {
  BuildObjects,
  ResourceObjects,
  TokenValleyBlockType,
} from "../types/tokenValley";
import { formatTimeDMS } from "../utils/formatTimeDMS";
import { formatTypesName } from "../utils/formatTypesName";
import { getResourceGatheringIconResourcesTypes } from "../utils/getResourceGatheringIcon";
import IconBlock from "./IconBlock";
import HandTutorial from "./ui/HandTutorial";

const Box = lazy(() => import("@mui/material/Box"));

interface TypesProps {
  open: boolean;
  playerDataStep: number;
  stepTutorial: StepTutorialTypes;
  setOpen: (open: boolean) => void;
  activeBlock: TokenValleyBlockType | null;
  setStepTutorial: (step: StepTutorialTypes) => void;
}

const DrawerBuildersHutHouse = ({
  open,
  setOpen,
  activeBlock,
  stepTutorial,
  playerDataStep,
  setStepTutorial,
}: TypesProps) => {
  const [gameConfig, playerData] = useUnit([$gameConfig, $playerData]);

  const [selectedBuild, setSelectedBuild] = useState<BuildObjects | null>(null);
  const excludedKeys = [
    "build/void",
    "build/token_house",
    "build/builders_hut",
  ];

  const allKeys = Object.keys(gameConfig?.buildings_spec || {}).filter(
    (key) => !excludedKeys.includes(key),
  );
  [allKeys[0], allKeys[2]] = [allKeys[2], allKeys[0]];

  const canPurchase = (selectedBuild: BuildObjects | null): boolean => {
    if (!selectedBuild) return false;

    const materials =
      gameConfig?.buildings_spec?.[selectedBuild]?.production_levels[1].build_cost?.materials;

    if (!materials) return false;

    return Object.entries(materials).every(
      ([material, requiredAmount]) =>
        (playerData.player_resources?.[material] ?? 0) >= (requiredAmount ?? 0),
    );
  };

  const handleBuild = (build: BuildObjects) => {
    if (!activeBlock) return;
    setOpen(false);

    buildValleyBlock({
      position: activeBlock.block_position,
      block_type: build,
    });

    setSelectedBuild(null);
  };
  const handleBlockClick = (build: BuildObjects) => {
    if (stepTutorial === STEP_TUTORIAL.CLICK_PLACE && build !== "build/farm") {
      return;
    }
    if (stepTutorial === STEP_TUTORIAL.CLICK_PLACE && build === "build/farm") {
      setStepTutorial(STEP_TUTORIAL.SELECT_BUILDING);
    }
    setSelectedBuild(build);
    if (getAmountOfBuildings(build) > 0) {
      handleBuild(build);
      setOpen(false);
      setSelectedBuild(null);
    }
  };

  return (
    <Drawer
      open={open}
      anchor="bottom"
      onClose={() => {
        if (
          stepTutorial === STEP_TUTORIAL.CLICK_PLACE &&
          playerDataStep !== -2
        ) {
          return;
        }
        setOpen(false);
      }}
      PaperProps={{
        sx: {
          pb: 2,
          borderTop: "3px solid",
          background: "linear-gradient( to top, #89a8dc, #7FA0DC)",
          borderColor: "primary.borderSideBar",
        },
      }}
    >
      <Suspense fallback={<CircularProgress />}>
        <Box p={2} position={"relative"} overflow={"hidden"}>
          <Typography pb={2} color="white">
            Select a building :
          </Typography>
          <Box
            pr={5}
            gap={1}
            display={"flex"}
            width={"100dvw"}
            position={"relative"}
            sx={{
              overflowY: "hidden",
              overflowX: "scroll",
              scrollbarWidth: "none",
              "&::-webkit-scrollbar": {
                display: "none",
              },
            }}
          >
            {allKeys.map((key) => (
              <Box key={key}>
                <Box
                  width={"115px"}
                  height={"105px"}
                  borderRadius={"8px"}
                  position={"relative"}
                  onClick={() => handleBlockClick(key)}
                  bgcolor={
                    selectedBuild === key ? "primary.main" : "transparent"
                  }
                >
                  <Box
                    width={"105px"}
                    position={"absolute"}
                    bottom={15}
                    left={20}
                  >
                    <IconBlock blockType={key} bigSize />
                  </Box>
                  {getAmountOfBuildings(key) > 0 && (
                    <Typography
                      color="white"
                      position={"absolute"}
                      right={10}
                      top={10}
                      textAlign={"center"}
                      sx={{
                        textShadow: `
                      -1.2px -1.2px 0.1px #344871,
                      1.2px -1.2px 0.1px #344871,
                      -1.2px 1.2px 0.1px #344871,
                      1.2px 1.2px 0.1px #344871
                    `,
                      }}
                    >
                      {getAmountOfBuildings(key)}
                    </Typography>
                  )}
                </Box>

                <Typography
                  color="white"
                  textAlign={"center"}
                  fontSize={selectedBuild === key ? "16px" : "14px"}
                  sx={{
                    textShadow: `
                      -1.2px -1.2px 0.1px #344871,
                      1.2px -1.2px 0.1px #344871,
                      -1.2px 1.2px 0.1px #344871,
                      1.2px 1.2px 0.1px #344871
                    `,
                  }}
                >
                  {formatTypesName(key)}
                </Typography>
                {key === "build/farm" &&
                  stepTutorial === STEP_TUTORIAL.CLICK_PLACE &&
                  playerDataStep !== -2 && (
                    <HandTutorial bottom="15px" right="30px" />
                  )}
              </Box>
            ))}
          </Box>
          {selectedBuild && (
            <Box bgcolor={"primary.light"} borderRadius={"8px"} p={2} mt={2}>
              <Typography
                color="white"
                width={"100%"}
                fontSize={"16px"}
                textAlign={"center"}
                sx={{
                  textShadow: `
                      -1.2px -1.2px 0.1px #344871,
                      1.2px -1.2px 0.1px #344871,
                      -1.2px 1.2px 0.1px #344871,
                      1.2px 1.2px 0.1px #344871
                    `,
                }}
              >
                {formatTypesName(selectedBuild)}
              </Typography>
              {gameConfig?.buildings_spec[selectedBuild]?.production_levels[1].build_cost &&
                Object.entries(
                  gameConfig.buildings_spec[selectedBuild].production_levels[1].build_cost.materials,
                ).map(([material, amount]) => (
                  <Box key={material}>
                    <Box
                      display={"flex"}
                      justifyContent={"space-between"}
                      alignItems={"center"}
                    >
                      <Box display={"flex"} alignItems={"center"} gap={2}>
                        <img
                          style={{ height: "40px" }}
                          src={
                            getResourceGatheringIconResourcesTypes(
                              material as ResourceObjects,
                            ) || ""
                          }
                          alt=""
                        />
                        <Typography color="white">
                          {formatTypesName(material)} :
                        </Typography>
                      </Box>
                      <Typography
                        color={
                          Number(playerData.player_resources?.[material]) <
                          (amount ?? 0)
                            ? "#E44754"
                            : "white"
                        }
                      >
                        {playerData.player_resources?.[material]?.toFixed(0) ??
                          "0"}{" "}
                        / {amount}
                      </Typography>
                    </Box>
                  </Box>
                ))}
              {gameConfig?.buildings_spec[selectedBuild]?.production_levels[1].build_cost.time && (
                <Box display={"flex"} justifyContent={"space-between"} mt={2}>
                  <Typography
                    color="white"
                    sx={{
                      textShadow: `
                      -1.2px -1.2px 0.1px #344871,
                      1.2px -1.2px 0.1px #344871,
                      -1.2px 1.2px 0.1px #344871,
                      1.2px 1.2px 0.1px #344871
                    `,
                    }}
                  >
                    Time:
                  </Typography>
                  <Typography>
                    <Typography
                      color="white"
                      sx={{
                        textShadow: `
                      -1.2px -1.2px 0.1px #344871,
                      1.2px -1.2px 0.1px #344871,
                      -1.2px 1.2px 0.1px #344871,
                      1.2px 1.2px 0.1px #344871
                    `,
                      }}
                    >
                      {formatTimeDMS(
                        gameConfig?.buildings_spec[selectedBuild]?.production_levels[1].build_cost
                          .time,
                      )}
                    </Typography>
                  </Typography>
                </Box>
              )}

              <Box
                mb={2}
                width={"100%"}
                display={"flex"}
                justifyContent={"space-between"}
              >
                <Typography
                  color="white"
                  sx={{
                    textShadow: `
                      -1.2px -1.2px 0.1px #344871,
                      1.2px -1.2px 0.1px #344871,
                      -1.2px 1.2px 0.1px #344871,
                      1.2px 1.2px 0.1px #344871
                    `,
                  }}
                >
                  Available
                </Typography>
                <Typography
                  color="white"
                  sx={{
                    textShadow: `
                      -1.2px -1.2px 0.1px #344871,
                      1.2px -1.2px 0.1px #344871,
                      -1.2px 1.2px 0.1px #344871,
                      1.2px 1.2px 0.1px #344871
                    `,
                  }}
                >
                  {getAmountOfBuildings(selectedBuild)}
                </Typography>
              </Box>

              <Button
                onClick={() => handleBuild(selectedBuild)}
                variant="blue"
                sx={{ width: "100%", height: "40px" }}
                disabled={!canPurchase(selectedBuild)}
              >
                Build
              </Button>
            </Box>
          )}
        </Box>
      </Suspense>
    </Drawer>
  );
};
export default DrawerBuildersHutHouse;
